import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { PageData } from "../../hooks/useWebpageData";
import { myPageDataEnum, useWebpageData } from "../../hooks/useWebpageData";
import { Preview } from "./Preview";
import { RenderEditImage, RenderEditItem } from "./Edit/RenderEdits";

import "./PreviewWrapper.css";
import { Link, unstable_usePrompt } from "react-router-dom";
import { HiMiniWrenchScrewdriver } from "react-icons/hi2";
import { TfiClose } from "react-icons/tfi";
import { BsToggleOff, BsToggleOn } from "react-icons/bs";
import { FaDesktop, FaMobileAlt } from "react-icons/fa";
import { FaExternalLinkAlt } from "react-icons/fa";
import { MdLabelImportantOutline } from "react-icons/md";
import { PiPencilThin, PiPencilSlashThin } from "react-icons/pi";
import { BsChevronDown } from "react-icons/bs";

interface PreviewWrapperProps {
  editEnabled?: boolean;
  previewData?: PageData;
}

export default function PreviewWrapper({
  editEnabled = false,
  previewData,
}: PreviewWrapperProps) {
  const { myPageData, saveMyPageData } = useWebpageData(previewData);
  const [editModeEnabled, setEditModeEnabled] = useState(editEnabled);
  const [desktopViewEnabled, setDesktopViewEnabled] = useState(true);

  // START********Preview Menu Deps********
  const ref = useRef<HTMLDivElement>(null);
  const [openMenu, setOpenMenu] = useState(true);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    if (ref?.current) {
      setWidth(ref.current.clientWidth);
      // Close menu if initially in mobile view
      if (ref.current.clientWidth < 600) {
        setOpenMenu(false);
      }
    }
  }, []);

  function toggleOpenMenu(onlyMobile = false) {
    if (onlyMobile) {
      if (width < 600) {
        setOpenMenu(val => !val);
      }
    } else {
      setOpenMenu(val => !val);
    }
  }

  const isMobile = width < 600;
  // END********Preview Menu Deps********
  const [webpageEdited, setWebpageEdited] = useState(false);
  function webpageEditedAction() {
    setWebpageEdited(true);
  }
  // TODO: Replace with better implementation
  unstable_usePrompt({
    message: "You have unsaved changes, are you sure you want to leave?",
    when: webpageEdited,
  });

  function savePageData() {
    saveMyPageData();
    setWebpageEdited(false);
  }

  function toggleEditMode() {
    setEditModeEnabled(val => !val);
  }

  // TODO: This needs to change the view port width to mimic mobile
  function toggleViewMode() {
    setDesktopViewEnabled(val => !val);
  }

  return (
    <div ref={ref} className="previewWrapper">
      <PreviewMenu
        openMenu={openMenu}
        toggleOpenMenu={toggleOpenMenu}
        savePageData={savePageData}
        myPageData={myPageData}
        webpageEdited={webpageEdited}
        webpageEditedAction={webpageEditedAction}
        isMobile={isMobile}
        editModeEnabled={editModeEnabled}
        toggleEditMode={toggleEditMode}
        desktopViewEnabled={desktopViewEnabled}
        toggleViewMode={toggleViewMode}
      />
      <div
        style={
          desktopViewEnabled
            ? { width: "100%" }
            : { marginLeft: "20%", width: "400px" }
        }
      >
        <Preview
          editModeEnabled={editModeEnabled}
          myPageData={myPageData}
          webpageEditedAction={webpageEditedAction}
        />
      </div>
    </div>
  );
}

interface PreviewMenuProps {
  openMenu: boolean;
  toggleOpenMenu: (onlyMobile?: boolean) => void;
  savePageData: () => void;
  myPageData: Map<any, any>;
  webpageEdited: boolean;
  webpageEditedAction: () => void;
  isMobile: boolean;
  editModeEnabled: boolean;
  toggleEditMode: () => void;
  desktopViewEnabled: boolean;
  toggleViewMode: () => void;
}

function PreviewMenu({
  openMenu,
  toggleOpenMenu,
  savePageData,
  myPageData,
  webpageEdited,
  webpageEditedAction,
  isMobile,
  editModeEnabled,
  toggleEditMode,
  desktopViewEnabled,
  toggleViewMode,
}: PreviewMenuProps) {
  const [openOption, setOpenOption] = useState("colors");

  function toggleCtaEnabled() {
    myPageData.get(myPageDataEnum.ctaEnabled)?.setter((val: boolean) => !val);
    webpageEditedAction();
  }

  function resetAccountInfo() {
    window.location.reload();
  }

  function toggleOption(optionId: string) {
    setOpenOption(prev => (prev === optionId ? "" : optionId));
  }

  return (
    <>
      {!openMenu && (
        <button
          type="button"
          className="sideBarMobileButtonFloat"
          onClick={() => toggleOpenMenu()}
        >
          <HiMiniWrenchScrewdriver />
        </button>
      )}
      <div className="sideBar" style={{ display: openMenu ? "block" : "none" }}>
        <div className="sideBarOptions">
          <div className="sideBarTitle">
            <h3>Webpage Options</h3>
            <button
              type="button"
              className="sideBarMobileButton"
              onClick={() => toggleOpenMenu()}
            >
              <TfiClose size={20} />
            </button>
          </div>
          <div className="sideBarDash" />
          <div className="sideBarOption sideBarOptionRow optionWithSpace">
            {webpageEdited && (
              <span className="buttonGroup">
                <button
                  className="mainButton"
                  type="button"
                  onClick={() => savePageData()}
                >
                  Save Changes
                </button>{" "}
                <button className="altButton" onClick={resetAccountInfo}>
                  Reset Changes
                </button>
              </span>
            )}
            {!webpageEdited && (
              <Link className="altButton" type="button" to="/settings">
                Publish / Settings
              </Link>
            )}
          </div>
          <div className="sideBarOption">
            <span className="sideBarEditMode">
              <b className="publishStatus">
                {editModeEnabled ? <PiPencilThin /> : <PiPencilSlashThin />}
              </b>
              Edit Mode:
              <button type="button" onClick={() => toggleEditMode()}>
                {editModeEnabled ? <BsToggleOn /> : <BsToggleOff />}
              </button>
              <b className="publishStatus">
                {editModeEnabled ? "(Edit)" : "(Preview)"}
              </b>
            </span>
            {!isMobile && (
              <span className="sideBarEditMode">
                <b className="publishStatus">
                  {desktopViewEnabled ? <FaDesktop /> : <FaMobileAlt />}
                </b>
                View Mode (coming soon):
                <button
                  disabled
                  type="button"
                  onClick={() => toggleViewMode()}
                  style={{ cursor: "not-allowed" }}
                >
                  {desktopViewEnabled ? <BsToggleOn /> : <BsToggleOff />}
                </button>
              </span>
            )}
          </div>
          <AccordionOption
            title="Colors"
            isOpen={openOption === "colors"}
            onToggle={() => toggleOption("colors")}
          >
            <h3>Primary Color</h3>
            <span className="sideBarOptionRow">
              <input
                type="color"
                value={myPageData.get(myPageDataEnum.primaryColor).value}
                onChange={e =>
                  myPageData
                    .get(myPageDataEnum.primaryColor)
                    .setter(e.currentTarget.value)
                }
                onClick={webpageEditedAction}
              />
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.primaryColor)}
                saveAction={webpageEditedAction}
              />
            </span>
            <h3>Secondary Color</h3>
            <span className="sideBarOptionRow">
              <input
                type="color"
                value={myPageData.get(myPageDataEnum.secondaryColor).value}
                onChange={e =>
                  myPageData
                    .get(myPageDataEnum.secondaryColor)
                    .setter(e.currentTarget.value)
                }
                onClick={webpageEditedAction}
              />
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.secondaryColor)}
                saveAction={webpageEditedAction}
              />
            </span>
          </AccordionOption>
          <AccordionOption
            title="Logo"
            isOpen={openOption === "logo"}
            onToggle={() => toggleOption("logo")}
          >
            <p>
              Logo lives on: <b>Top Title</b>, <b>'About' section</b>, and{" "}
              <b>Footer</b>.
            </p>
            <div className="sideBarLogo">
              <RenderEditImage
                inputSrc={myPageData.get(myPageDataEnum.logo)}
                altText="logo"
                saveAction={webpageEditedAction}
              />
            </div>
          </AccordionOption>
          <AccordionOption
            title="Background"
            isOpen={openOption === "background"}
            onToggle={() => toggleOption("background")}
          >
            <div
              className={
                myPageData.get(myPageDataEnum.backgroundImg)?.value
                  ? "settingsEditBackground"
                  : "settingsEditBackground coverBgPatternSmall"
              }
            >
              <RenderEditImage
                inputSrc={myPageData.get(myPageDataEnum.backgroundImg)}
                altText="Background"
                saveAction={webpageEditedAction}
                imageSizeRecommendation={{
                  width: 1000,
                  height: 500,
                }}
              />
            </div>
          </AccordionOption>
          <AccordionOption
            title="Tips"
            isOpen={openOption === "tips"}
            onToggle={() => toggleOption("tips")}
          >
            <ul>
              <li>
                Toggle the <b>Edit Mode</b> for "live" preview of styling and
                functionality.
              </li>
              <li>
                Surround words with brackets <b>[one, two, three]</b>{" "}
                {"-*becomes*-> "}
                <RenderEditItem
                  inputItem={{
                    value: "[one, two, three]",
                    setter: () => null,
                  }}
                  editModeEnabled={openOption !== "tips"}
                  accentColor="#0538af"
                />{" "}
                for an automatic slide-show effect ending on the last word.
              </li>
              <li>
                Include `http` with links to make them clickable:{" "}
                <b>https://secure.easyzilla.com/webpage#top</b>
                {" -*becomes*-> "}
                <RenderEditItem
                  inputItem={{
                    value: "https://secure.easyzilla.com/webpage#top",
                    setter: () => null,
                  }}
                  editModeEnabled={false}
                />
                .
              </li>
              <li>
                If this is a landing page, use the <b>CTA</b> functionality
                below to set up an action you would like your users to complete.
              </li>
            </ul>
          </AccordionOption>
          <AccordionOption
            title="CTA (Call To Action)"
            isOpen={openOption === "cta"}
            onToggle={() => toggleOption("cta")}
          >
            <p>
              CTA (button) will be shown right of the menu and under the header.
              Can be used to redirect to the link you provide. Example Cta Link{" "}
              <i>https://secure.easyzilla.com/settings</i>.
            </p>
            <span className="sideBarEditMode">
              <b className="publishStatus">
                {myPageData.get(myPageDataEnum.ctaEnabled)?.value
                  ? "Enabled"
                  : "Disabled"}
              </b>
              <button type="button" onClick={() => toggleCtaEnabled()}>
                {myPageData.get(myPageDataEnum.ctaEnabled)?.value ? (
                  <BsToggleOn />
                ) : (
                  <BsToggleOff />
                )}
              </button>
            </span>
            <span className="sideBarOptionRow">
              <MdLabelImportantOutline />
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.ctaText)}
                saveAction={webpageEditedAction}
                placeholder="cta label"
              />
            </span>
            <span className="sideBarOptionRow">
              <FaExternalLinkAlt />
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.ctaLink)}
                saveAction={webpageEditedAction}
                placeholder="cta link"
                inputType="url"
              />
            </span>
          </AccordionOption>
        </div>
      </div>
    </>
  );
}

interface AccordionOptionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function AccordionOption({
  title,
  isOpen,
  onToggle,
  children,
}: AccordionOptionProps) {
  return (
    <div className={`sideBarOption ${isOpen ? "sideBarOptionOpen" : ""}`}>
      <button
        type="button"
        className="sideBarOptionHeader"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <h3>{title}</h3>
        <BsChevronDown className="sideBarOptionChevron" />
      </button>
      <div
        className={`sideBarOptionContentWrapper ${
          isOpen ? "sideBarOptionContentOpen" : ""
        }`}
      >
        <div className="sideBarOptionContent">{children}</div>
      </div>
    </div>
  );
}
